import React from "react";
import { Paper, Typography, Box, Grid } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

// Registrar los elementos de Chart.js
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1)
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(3)
  },
  chartContainer: {
    position: "relative",
    height: "300px",
    padding: theme.spacing(2)
  }
}));

const ChartsSection = ({ metrics, startDate, endDate, selectedCampaignId }) => {
  const classes = useStyles();
  const theme = useTheme();

  if (!metrics) {
    return null;
  }

  // Configuración de colores
  const successColor = "#4caf50";
  const errorColor = "#f44336";
  const infoColor = "#2196f3";

  // Gráfico 1: Distribución de Estados (Doughnut)
  const statusDistributionData = {
    labels: ["Entregados", "Fallidos"],
    datasets: [
      {
        data: [metrics.totalDelivered || 0, metrics.totalFailed || 0],
        backgroundColor: [successColor, errorColor],
        borderColor: [successColor, errorColor],
        borderWidth: 2
      }
    ]
  };

  const statusDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: theme.palette.text.primary,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: false
      }
    }
  };

  // Gráfico 2: Envíos por Hora del Día (Bar)
  const peakHoursData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [
      {
        label: "Envíos",
        data: Array.from({ length: 24 }, (_, i) => {
          const hourData = metrics.peakHours?.find((h) => h.hour === i);
          return hourData ? hourData.count : 0;
        }),
        backgroundColor: infoColor,
        borderColor: infoColor,
        borderWidth: 1
      }
    ]
  };

  const peakHoursOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.secondary,
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          color: theme.palette.divider
        }
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
          beginAtZero: true
        },
        grid: {
          color: theme.palette.divider
        }
      }
    }
  };

  // Gráfico 3: Tasa de Éxito (Line - simulado para mostrar tendencia)
  const successRateData = {
    labels: ["Inicio", "Progreso", "Actual"],
    datasets: [
      {
        label: "Tasa de Éxito (%)",
        data: [
          0,
          metrics.successRate ? metrics.successRate / 2 : 0,
          metrics.successRate || 0
        ],
        fill: true,
        backgroundColor: `${successColor}33`,
        borderColor: successColor,
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: successColor,
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 5
      }
    ]
  };

  const successRateOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.secondary
        },
        grid: {
          color: theme.palette.divider
        }
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
          beginAtZero: true,
          max: 100,
          callback: function (value) {
            return value + "%";
          }
        },
        grid: {
          color: theme.palette.divider
        }
      }
    }
  };

  return (
    <Paper className={classes.panel}>
      <Typography variant="h6" className={classes.title}>
        <span role="img" aria-label="gráfico de barras">📈</span> Análisis y Gráficos
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="subtitle1" gutterBottom align="center" style={{ fontWeight: 600 }}>
              Distribución de Estados
            </Typography>
            <Box className={classes.chartContainer}>
              <Doughnut data={statusDistributionData} options={statusDistributionOptions} />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box>
            <Typography variant="subtitle1" gutterBottom align="center" style={{ fontWeight: 600 }}>
              Envíos por Hora del Día
            </Typography>
            <Box className={classes.chartContainer}>
              <Bar data={peakHoursData} options={peakHoursOptions} />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box>
            <Typography variant="subtitle1" gutterBottom align="center" style={{ fontWeight: 600 }}>
              Tendencia de Tasa de Éxito
            </Typography>
            <Box className={classes.chartContainer}>
              <Line data={successRateData} options={successRateOptions} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ChartsSection;

